import React from 'react';
import BlockedReasonModal from '../BlockedReasonModal';
import ConfirmModal from '../FlowchartView/components/ConfirmModal';
import ArtifactSummary from '../FlowchartView/components/ArtifactSummary';
import TaskDetailSidebar from '../FlowchartView/components/TaskDetailSidebar';
import AttachmentsModal from '../shared/AttachmentsModal';
import DeliverablesModal from '../shared/DeliverablesModal';
import DeliverableSubmitModal from '../shared/DeliverableSubmitModal';
import InProgressLockWarningModal from '../FlowchartView/components/InProgressLockWarningModal';
import CustomFieldsManager from '../shared/CustomFieldsManager';
import RenameFieldModal from '../shared/CustomFieldsManager/RenameFieldModal';

/**
 * ModalsContainer
 * 
 * Handles rendering of all modals and sidebars for SprintDetail.
 * Keeps the main component focused on orchestration.
 */
const ModalsContainer = ({
  // Blocked modal
  blockedModalState,
  onBlockedModalClose,
  onBlockedConfirm,
  
  // Delete confirmation modal
  deleteModalState,
  onDeleteModalConfirm,
  onDeleteModalClose,

  // Delete all missions confirmation (Ctrl+A + Delete)
  deleteAllModalState,
  onDeleteAllModalConfirm,
  onDeleteAllModalClose,

  // Reset campaign progress confirmation
  resetCampaignModalState,
  onResetCampaignModalConfirm,
  onResetCampaignModalClose,
  
  // Date validation error modal
  dateValidationError,
  onDateValidationErrorClose,
  
  // Artifact summary
  tasks,
  isArtifactSummaryOpen,
  onArtifactSummaryClose,
  
  // Task detail sidebar
  detailSidebarTaskId,
  onDetailSidebarClose,
  missionDocStack = [],
  missionDocDirect = false,
  onMissionDocStackChange,
  skipSurfaceAnimation = false,
  onSkipSurfaceAnimationConsumed,
  allCategories,
  allTags,
  statuses,
  lockStates,
  nestedSprints,
  allSprints = [],
  currentView,
  flowchartRef,
  onStatusChangeWithModal,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onCategoryChange,
  onRenameTask,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Tag callbacks
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  
  // Attachments modal
  attachmentsModalTaskId,
  onAttachmentsModalClose,
  onAttachmentsChange,
  
  // Deliverables modal
  deliverablesModalTaskId,
  onDeliverablesModalClose,
  onAddDeliverable,
  onRemoveDeliverable,
  onUpdateDeliverable,
  
  // In-progress lock warning modal
  inProgressLockWarningModal,
  // Custom fields manager modal
  isCustomFieldsManagerOpen,
  onCustomFieldsManagerClose,
  customFields,
  onCustomFieldsChange,
  // Custom field value changes (for detail sidebar)
  onCustomFieldValueChange,
  // Custom field rename modal (for paste conflicts)
  renameFieldModal,
  onRenameFieldModalClose,
  // Breadcrumb navigation for detail sidebar
  breadcrumbPath,
  currentCampaign,
  onSelectSprint,
  onOpenNestedSprint,
  onRemoveNestedSprint,
  onDeleteTask,
  onMissionDocTicksChange,
  onMissionDocFilesChange,
  onMissionVariablesChange,
  onAttachedDocsChange,
  onQuickNotesChange,
  onMissionDocsMigrated,
  onClearMissionLegacyNotes,
  // Variable resolution context for mission-attached docs
  variableCampaign,
  onCampaignVariablesChange,
  onPatchInheritedVariable,
  // Conditions editing
  onConditionsChange,
  // Deliverables editing
  onEditDeliverables,
  // Deliverable submission modal
  deliverableSubmitModal,
  onDeliverableSubmitModalClose,
  onSubmitDeliverableLink,
  onSubmitDeliverableAttachment,
  // Clear deliverable submission
  onClearDeliverable,
}) => {
  // Keep a stable stub while the target mission isn't in `tasks` yet (tab
  // switch / campaign still loading). Returning null here unmounts the doc
  // sheet and causes a skeleton → disappear → reappear flash.
  const sidebarTask = detailSidebarTaskId
    ? (tasks.find(t => t.id === detailSidebarTaskId) || {
        id: detailSidebarTaskId,
        name: '',
      })
    : null;

  const attachmentsTask = attachmentsModalTaskId
    ? tasks.find(t => t.id === attachmentsModalTaskId)
    : null;

  const deliverablesTask = deliverablesModalTaskId
    ? tasks.find(t => t.id === deliverablesModalTaskId)
    : null;

  return (
    <>
      {/* Blocked Reason Modal */}
      <BlockedReasonModal
        isOpen={blockedModalState.isOpen}
        onClose={onBlockedModalClose}
        onConfirm={onBlockedConfirm}
        taskName={blockedModalState.taskName}
      />

      {/* Delete confirmation modal for tasks with nested campaigns */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        title="Move to Trash"
        message={`Move "${deleteModalState.nestedSprintName}" to trash? This will also delete the node from the current campaign. You can restore it later from the Trash.`}
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="🗑"
        onConfirm={onDeleteModalConfirm}
        onCancel={onDeleteModalClose}
      />

      <ConfirmModal
        isOpen={deleteAllModalState.isOpen}
        title="Delete all missions?"
        message={`You're about to delete all ${deleteAllModalState.taskCount} mission${deleteAllModalState.taskCount === 1 ? '' : 's'} in "${deleteAllModalState.campaignName}". This cannot be undone.`}
        confirmLabel="Delete all"
        cancelLabel="Cancel"
        confirmVariant="danger"
        icon="⚠"
        onConfirm={onDeleteAllModalConfirm}
        onCancel={onDeleteAllModalClose}
      />

      <ConfirmModal
        isOpen={!!resetCampaignModalState?.isOpen}
        title={resetCampaignModalState?.isMulti ? 'Reset progress in this view?' : 'Reset this campaign?'}
        message={
          resetCampaignModalState?.isMulti
            ? 'Are you sure? Decisions will be cleared and missions set back to To Do on their source campaigns.'
            : 'Are you sure? Decisions will be cleared, missions set back to To Do, and nested campaigns under this tree are included.'
        }
        confirmLabel="Do It."
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="↺"
        confirmDisabled={!!resetCampaignModalState?.loading}
        onConfirm={onResetCampaignModalConfirm}
        onCancel={onResetCampaignModalClose}
      >
        <ul className="confirm-modal__impact-list confirm-modal__impact-list--reset">
          {resetCampaignModalState?.isMulti ? (
            <li>
              {(resetCampaignModalState.rootMissionCount || 0) > 0
                ? `${resetCampaignModalState.rootMissionCount} mission${
                    resetCampaignModalState.rootMissionCount === 1 ? '' : 's'
                  } will be reset to To Do`
                : 'No missions in this view to reset'}
            </li>
          ) : (
            <>
              <li>
                {(resetCampaignModalState?.rootMissionCount || 0) > 0
                  ? `${resetCampaignModalState.rootMissionCount} mission${
                      resetCampaignModalState.rootMissionCount === 1 ? '' : 's'
                    } in this campaign will be reset to To Do`
                  : 'No missions in this campaign to reset'}
              </li>
              <li>
                {(resetCampaignModalState?.nestedMissionCount || 0) > 0
                  ? `${resetCampaignModalState.nestedMissionCount} mission${
                      resetCampaignModalState.nestedMissionCount === 1 ? '' : 's'
                    } in nested campaigns will be reset to To Do`
                  : 'No missions in nested campaigns to reset'}
              </li>
              <li>
                {(resetCampaignModalState?.variableValueCount || 0) > 0
                  ? `${resetCampaignModalState.variableValueCount} variable value${
                      resetCampaignModalState.variableValueCount === 1 ? '' : 's'
                    } will be cleared (names kept)`
                  : 'No variable values to clear'}
              </li>
            </>
          )}
          <li className="confirm-modal__impact-docs" aria-busy={!!resetCampaignModalState?.loading}>
            {resetCampaignModalState?.loading ? (
              <div className="confirm-modal__impact-shimmer-stack" aria-hidden="true">
                <div
                  className="confirm-modal__impact-shimmer"
                  style={{ width: '92%', animationDelay: '0s' }}
                />
                <div
                  className="confirm-modal__impact-shimmer"
                  style={{ width: '64%', animationDelay: '0.06s' }}
                />
              </div>
            ) : (resetCampaignModalState?.checkboxCount || 0) > 0 ? (
              `${resetCampaignModalState.checkboxCount} checkbox${
                resetCampaignModalState.checkboxCount === 1 ? '' : 'es'
              } across ${resetCampaignModalState.documentCount} doc${
                resetCampaignModalState.documentCount === 1 ? '' : 's'
              } will be unchecked`
            ) : (
              'No checked boxes in attached documents'
            )}
          </li>
          <li>
            {(resetCampaignModalState?.fileCount || 0) > 0
              ? `${resetCampaignModalState.fileCount} upload-field file${
                  resetCampaignModalState.fileCount === 1 ? '' : 's'
                } will be cleared from missions`
              : 'No upload-field files to clear'}
          </li>
        </ul>
      </ConfirmModal>

      {/* Date validation error modal */}
      <ConfirmModal
        isOpen={!!dateValidationError}
        title={dateValidationError?.title || 'Date Conflict'}
        message={dateValidationError?.message || 'The date change is not allowed.'}
        confirmLabel="OK"
        cancelLabel={null}
        confirmVariant="warning"
        icon="📅"
        onConfirm={onDateValidationErrorClose}
        onCancel={onDateValidationErrorClose}
      />

      {/* Artifact Summary */}
      <ArtifactSummary
        tasks={tasks}
        isOpen={isArtifactSummaryOpen}
        onClose={onArtifactSummaryClose}
      />

      {/* Task Detail Sidebar */}
      <TaskDetailSidebar
        isOpen={!!detailSidebarTaskId}
        onClose={onDetailSidebarClose}
        task={sidebarTask}
        allTasks={tasks}
        allCategories={allCategories}
        allTags={allTags}
        statuses={statuses}
        lockStates={lockStates || {}}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        initialDocStack={missionDocStack}
        missionDocDirect={missionDocDirect}
        onDocStackChange={onMissionDocStackChange}
        skipSurfaceAnimation={skipSurfaceAnimation}
        onSkipSurfaceAnimationConsumed={onSkipSurfaceAnimationConsumed}
        breadcrumbPath={breadcrumbPath}
        currentCampaign={currentCampaign}
        onSelectSprint={onSelectSprint}
        onGoToTask={(taskId) => {
          if (currentView === 'flow') {
            flowchartRef.current?.focusTask(taskId);
          }
          onDetailSidebarClose();
        }}
        onStatusChange={onStatusChangeWithModal}
        onPriorityChange={onPriorityChange}
        onDifficultyChange={onDifficultyChange}
        onDueDateChange={onDueDateChange}
        onRemindersChange={onRemindersChange}
        onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
        onCreatedAtChange={onCreatedAtChange}
        onStartedAtChange={onStartedAtChange}
        onCompletedAtChange={onCompletedAtChange}
        onUpdatedAtChange={onUpdatedAtChange}
        onCategoryChange={onCategoryChange}
        onRenameTask={onRenameTask}
        onIconChange={onIconChange}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        onSetStatusAutoSync={onSetStatusAutoSync}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
        onSetPrimaryTag={onSetPrimaryTag}
        onRenameTag={onRenameTag}
        onDeleteTag={onDeleteTag}
        onOpenNestedSprint={onOpenNestedSprint}
        onRemoveNestedSprint={onRemoveNestedSprint}
        onDeleteTask={onDeleteTask}
        onMissionDocTicksChange={onMissionDocTicksChange}
        onMissionDocFilesChange={onMissionDocFilesChange}
        onMissionVariablesChange={onMissionVariablesChange}
        onAttachedDocsChange={onAttachedDocsChange}
        onQuickNotesChange={onQuickNotesChange}
        onMissionDocsMigrated={onMissionDocsMigrated}
        onClearMissionLegacyNotes={onClearMissionLegacyNotes}
        variableCampaign={variableCampaign}
        onCampaignVariablesChange={onCampaignVariablesChange}
        onPatchInheritedVariable={onPatchInheritedVariable}
        customFields={customFields}
        onCustomFieldValueChange={onCustomFieldValueChange}
        onConditionsChange={onConditionsChange}
        onEditDeliverables={onEditDeliverables}
        onSubmitDeliverable={deliverableSubmitModal?.onOpenSubmitModal}
        onClearDeliverable={(deliverable) => {
          if (detailSidebarTaskId && onClearDeliverable) {
            onClearDeliverable(detailSidebarTaskId, deliverable.id);
          }
        }}
      />

      {/* Attachments Modal */}
      <AttachmentsModal
        isOpen={!!attachmentsModalTaskId}
        onClose={onAttachmentsModalClose}
        taskId={attachmentsTask?.id}
        taskName={attachmentsTask?.name || 'Mission'}
        onAttachmentsChange={onAttachmentsChange}
      />

      {/* Deliverables Modal */}
      <DeliverablesModal
        isOpen={!!deliverablesModalTaskId}
        onClose={onDeliverablesModalClose}
        taskName={deliverablesTask?.name || 'Mission'}
        deliverables={deliverablesTask?.deliverables || []}
        onAddDeliverable={(data) => onAddDeliverable?.(deliverablesModalTaskId, data)}
        onRemoveDeliverable={(deliverableId) => onRemoveDeliverable?.(deliverablesModalTaskId, deliverableId)}
        onUpdateDeliverable={(deliverableId, data) => onUpdateDeliverable?.(deliverablesModalTaskId, deliverableId, data)}
      />

      {/* Deliverable Submit Modal */}
      <DeliverableSubmitModal
        isOpen={deliverableSubmitModal?.isOpen}
        onClose={onDeliverableSubmitModalClose}
        taskId={deliverableSubmitModal?.taskId}
        taskName={deliverableSubmitModal?.taskName}
        deliverable={deliverableSubmitModal?.deliverable}
        onSubmitLink={onSubmitDeliverableLink}
        onSubmitAttachment={onSubmitDeliverableAttachment}
        onAttachmentsChange={() => {
          // When a file is uploaded, update attachment count
          const task = tasks.find(t => t.id === deliverableSubmitModal?.taskId);
          if (task) {
            onAttachmentsChange?.((task.attachmentCount || 0) + 1);
          }
        }}
      />

      {/* In-Progress Lock Warning Modal */}
      {inProgressLockWarningModal && (
        <InProgressLockWarningModal
          isOpen={inProgressLockWarningModal.isOpen}
          affectedTask={inProgressLockWarningModal.affectedTask}
          blockingTasks={inProgressLockWarningModal.blockingTasks}
          statuses={inProgressLockWarningModal.statuses}
          allTags={inProgressLockWarningModal.allTags}
          onConfirm={inProgressLockWarningModal.onConfirm}
          onCancel={inProgressLockWarningModal.onCancel}
        />
      )}

      {/* Custom Fields Manager Modal */}
      <CustomFieldsManager
        isOpen={isCustomFieldsManagerOpen}
        onClose={onCustomFieldsManagerClose}
        customFields={customFields}
        allTasks={tasks}
        onCustomFieldsChange={onCustomFieldsChange}
      />

      {/* Custom Field Rename Modal (for paste conflicts) */}
      {renameFieldModal && (
        <RenameFieldModal
          isOpen={!!renameFieldModal}
          conflictingField={renameFieldModal.clipboardField}
          existingFieldType={renameFieldModal.existingField?.type}
          existingFieldNames={renameFieldModal.existingFieldNames}
          onRename={(newName) => {
            renameFieldModal.resolve(newName);
            onRenameFieldModalClose();
          }}
          onCancel={() => {
            renameFieldModal.reject(new Error('User cancelled'));
            onRenameFieldModalClose();
          }}
        />
      )}
    </>
  );
};

export default ModalsContainer;
